import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Box, ButtonBase, Divider, Grid2, SxProps, Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { AlertFilter } from 'shared';
import { useMemo } from 'react';
import { ILockerGateItem } from 'api/interfaces/lockerGate';
import { filterDataByAlertFilter } from '../alerts.logic';
import { StyledResponsiveButton } from '../StyledComponents';
import outOfUsePattern from 'assets/outOfUsePattern.png';

// Locker-status filters reuse the exact AP badge palette (see RowItemPill) so a filter's colour
// matches the badge colour shown on the rows it surfaces. OutOfUse uses the striped pattern instead.
const FILTER_COLORS: Record<AlertFilter, string> = {
	[AlertFilter.Opened]: '#18A900',
	[AlertFilter.Unpaid]: '#bb2828',
	[AlertFilter.Rented]: '#AED87A',
	[AlertFilter.PendingVerification]: '#EB8BED',
	[AlertFilter.Available]: '#8EC8FF',
	[AlertFilter.OutOfUse]: '#8F8F8F',
	[AlertFilter.Padlock]: '#bb2828',
	[AlertFilter.Gates]: '#bb2828',
	[AlertFilter.All]: '#D9D9D9',
};

export type ActivePreset = 'all' | 'onSite' | null;

interface FiltersProps {
	activeFilters: Set<AlertFilter>;
	onFilterClick: (filter: AlertFilter) => void;
	onApplyAll: () => void;
	onApplyOnSiteActions: () => void;
	activePreset: ActivePreset;
	alerts: ILockerGateItem[] | undefined;
	padlockEnabled: boolean;
}

const presetSx = (isActive: boolean): SxProps<Theme> => ({
	borderRadius: '20px',
	borderColor: isActive ? '#9e9e9e' : '#D9D9D9',
	color: isActive ? '#222' : '#333',
	backgroundColor: isActive ? '#cfcfcf' : 'white',
	textTransform: 'none',
	fontWeight: isActive ? 600 : 500,
	'&:hover': { borderColor: '#9e9e9e', backgroundColor: isActive ? '#c4c4c4' : '#f5f5f5' },
});

export const Filters = ({ activeFilters, onFilterClick, onApplyAll, onApplyOnSiteActions, activePreset, alerts, padlockEnabled }: FiltersProps) => {
	const { t } = useTranslation();

	const getCount = (filter: AlertFilter) =>
		alerts?.filter((x) => filterDataByAlertFilter(x, filter)).length ?? 0;

	const toggles = useMemo(
		() => [
			{ value: AlertFilter.Opened, text: t('filters.opened'), showCount: true },
			{ value: AlertFilter.Unpaid, text: t('filters.unpaid'), showCount: true },
			{ value: AlertFilter.Rented, text: t('filters.rented'), showCount: true },
			{ value: AlertFilter.PendingVerification, text: t('filters.pendingVerification'), showCount: true },
			{ value: AlertFilter.Available, text: t('filters.available'), showCount: true },
			{ value: AlertFilter.OutOfUse, text: t('filters.outOfUse'), showCount: true },
			...(padlockEnabled ? [{ value: AlertFilter.Padlock, text: t('filters.padlock'), showCount: true }] : []),
			{ value: AlertFilter.Gates, text: t('filters.gates'), showCount: false },
		],
		[t, padlockEnabled]
	);

	return (
		<Box>
			<Grid2 container spacing={1} mb={1}>
				<Grid2 size={{ xs: 6, sm: 4 }}>
					<StyledResponsiveButton
						onClick={onApplyAll}
						fullWidth
						size='small'
						variant='outlined'
						sx={presetSx(activePreset === 'all')}
					>
						{t('filters.presetAll')}
					</StyledResponsiveButton>
				</Grid2>
				<Grid2 size={{ xs: 6, sm: 4 }}>
					<StyledResponsiveButton
						onClick={onApplyOnSiteActions}
						fullWidth
						size='small'
						variant='outlined'
						sx={presetSx(activePreset === 'onSite')}
					>
						{t('filters.presetOnSiteActions')}
					</StyledResponsiveButton>
				</Grid2>
			</Grid2>

			<Divider sx={{ mb: 1 }} />

			<Grid2 container spacing={1}>
				{toggles.map((toggle) => {
					const active = activeFilters.has(toggle.value);
					const color = FILTER_COLORS[toggle.value];
					const label = toggle.showCount
						? `${toggle.text} (${getCount(toggle.value)})`
						: toggle.text;

					// Kłódka and Bramy stay as plain outlined tiles; every other active filter fills its
					// tile with the status colour. OutOfUse mirrors the AP badge (RowItemPill): the striped
					// pattern is only a thin frame around a solid grey chip, so the label stays readable.
					const unfilled = toggle.value === AlertFilter.Padlock || toggle.value === AlertFilter.Gates;
					const filled = active && !unfilled;
					const patterned = filled && toggle.value === AlertFilter.OutOfUse;
					const onColor = filled && !patterned;

					const checkbox = !active ? (
						<CheckBoxOutlineBlankIcon sx={{ color: '#D9D9D9', fontSize: 20, flexShrink: 0 }} />
					) : (
						<CheckBoxIcon
							sx={{ color: onColor ? '#fff' : patterned ? '#8F8F8F' : color, fontSize: 20, flexShrink: 0 }}
						/>
					);

					const labelNode = (
						<Box
							component='span'
							sx={{
								fontSize: '11px',
								fontWeight: 600,
								letterSpacing: '0.3px',
								color: onColor ? '#fff' : active ? '#222' : '#888',
								lineHeight: 1.2,
								textAlign: 'left',
								overflow: 'hidden',
								textOverflow: 'ellipsis',
								whiteSpace: 'nowrap',
							}}
						>
							{label}
						</Box>
					);

					return (
						<Grid2 size={{ xs: 6, sm: 4 }} key={toggle.value}>
							<ButtonBase
								onClick={() => onFilterClick(toggle.value)}
								sx={{
									width: '100%',
									borderRadius: '8px',
									border: `2px solid ${active ? color : '#D9D9D9'}`,
									background: patterned ? `url(${outOfUsePattern})` : onColor ? color : 'white',
									padding: patterned ? '3px' : '5px 8px',
									display: 'flex',
									alignItems: 'center',
									gap: patterned ? 0 : '4px',
									justifyContent: 'flex-start',
									transition: 'border-color 0.15s, background 0.15s',
									opacity: active ? 1 : 0.5,
									minHeight: '32px',
								}}
							>
								{patterned ? (
									<Box
										sx={{
											display: 'flex',
											alignItems: 'center',
											gap: '4px',
											width: '100%',
											minWidth: 0,
											backgroundColor: '#F1F1F1',
											borderRadius: '6px',
											padding: '2px 6px',
										}}
									>
										{checkbox}
										{labelNode}
									</Box>
								) : (
									<>
										{checkbox}
										{labelNode}
									</>
								)}
							</ButtonBase>
						</Grid2>
					);
				})}
			</Grid2>
		</Box>
	);
};
